import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router, RouterOutlet } from '@angular/router';
import { takePromise } from '@y42/util/take-promise';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NamedRouterOutletService {
  constructor(private readonly router: Router) {}

  private readonly namedRouterOutlets$$: BehaviorSubject<
    Record<
      string,
      {
        outlet: RouterOutlet;
        route: ActivatedRoute;
      }
    >
  > = new BehaviorSubject({});

  /**
   * Register a named `router-outlet` and its corresponding root `ActivatedRoute`
   */
  register(outletName: string, outlet: RouterOutlet, route: ActivatedRoute) {
    const namedRouterOutlets = this.namedRouterOutlets$$.value;
    namedRouterOutlets[outletName] = { outlet, route };
    this.namedRouterOutlets$$.next(namedRouterOutlets);
  }

  /**
   * Emites a boolean if the named router-outlet is active or not
   */
  selectIsPathActive(outletName: string, path: string): Observable<boolean> {
    return this.selectOutletActivated(outletName).pipe(
      withLatestFrom(this.selectOutlet(outletName)),
      map(([open, outlet]) => open && outlet.activatedRoute.routeConfig?.path === path),
      distinctUntilChanged(),
    );
  }

  /**
   * Emits a boolean whenever the named `router`outlet`'s state changes,
   * whether a route is activated (true) or deactivated (false).
   */
  selectOutletActivated(outletName: string): Observable<boolean> {
    return this.selectOutlet(outletName).pipe(
      switchMap((outlet) =>
        merge(outlet.activateEvents, outlet.deactivateEvents).pipe(
          startWith(outlet.isActivated),
          map(() => this.getActivatedRoute(outletName)),
          map((route) => route?.children ?? []),
          map((children) => children.some((child) => child.outlet === outletName)),
          distinctUntilChanged(),
        ),
      ),
    );
  }

  /**
   * Select a named router-outlet's first child `ActivatedRoute`.
   */
  selectChildActivatedRoute(outletName: string): Observable<ActivatedRoute | null> {
    return this.selectOutletActivated(outletName).pipe(
      withLatestFrom(this.selectOutlet(outletName)),
      map(([_, outlet]) => {
        try {
          return outlet.activatedRoute.firstChild || null;
        } catch (err) {
          return null;
        }
      }),
    );
  }

  /**
   * Select a named router-outlet's root `ActivatedRoute`.
   */
  selectedRootActivatedRoute(outletName: string) {
    return this.namedRouterOutlets$$.pipe(
      map(() => this.getActivatedRoute(outletName)),
      filter(Boolean),
      distinctUntilChanged(),
    );
  }

  async navigate(outletName: string, commands: string[] | null, options?: NavigationExtras): Promise<boolean> {
    const relativeTo = await takePromise(this.selectedRootActivatedRoute(outletName));
    return await this.router.navigate(
      [
        {
          outlets: { [outletName]: commands },
        },
      ],
      {
        // !!! IMPORTANT !!!
        // The correct `relativeTo` `ActivatedRoute` object is the **key for correct navigation** in secondary router-outlets (sidebar, bottom_drawer).
        //
        // With the current route configuration and hierarchy in `feature.module` the correct `ActivatedRoute` object is the *parent* of the `FeatureComponent`.
        // If routing in the sidebar or bottom_drawer breaks play around with either `relativeTo.parent` or `firstChild` or `children`.
        relativeTo,
        queryParamsHandling: 'preserve',
        ...(options || {}),
      },
    );
  }

  /**
   * Get a named router-outlet's corresponding `ActivatedRoute`.
   */
  private getActivatedRoute(outletName: string) {
    return this.namedRouterOutlets$$.value[outletName]?.route;
  }

  /**
   * Select a `router-outlet` by name.
   */
  private selectOutlet(outletName: string): Observable<RouterOutlet> {
    return this.namedRouterOutlets$$.pipe(
      map(() => this.getOutlet(outletName)),
      filter(Boolean),
      distinctUntilChanged(),
    );
  }

  /**
   * Get a `router-outlet` by name.
   */
  private getOutlet(outletName: string) {
    return this.namedRouterOutlets$$.value[outletName]?.outlet;
  }
}
